import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataError } from "../../core/errors/DataError";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import {
  LogoMethodMaxSizeEntryDto,
  LogoMethodMaxSizesDto,
} from "../models/responses/LogoMethodMaxSizesDto";
import { LOGO_METHOD_MAX_SIZE_CONFLICT_ERROR_CODE } from "../models/mappers/mapError";
import { adminSwrOptions } from "./adminSwr";

export interface TransferMethodConfigRepository {
  useBlockedMethods(): Resource<string[]>;
  update(blockedMethodKeys: string[]): Promise<void>;
  /**
   * The admin-managed max logo size per method (flag
   * REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED - only the "Max logo size" box
   * calls it). null = the API's flag is off (404): no box, never a toast.
   */
  useMethodMaxSizes(): Resource<LogoMethodMaxSizesDto | null>;
  /**
   * Saves the WHOLE set (a method left out has no override) from the
   * revision the page holds. Resolves with the new server state (its
   * revision is the next save's). Somebody else saved in between = the API
   * wrote nothing and this rejects with LogoMethodMaxSizeConflictError; the
   * state the API answered with is already in the query cache then.
   */
  updateMethodMaxSizes(
    expectedRevision: number,
    sizes: LogoMethodMaxSizeEntryDto[],
  ): Promise<LogoMethodMaxSizesDto>;
}

const BLOCKED_METHODS_KEY = "blocked-transfer-methods";
export const LOGO_METHOD_MAX_SIZES_KEY = "logo-method-max-sizes";

/** ONLY the API's 404 (its flag off) means "no box"; anything else is an error. */
export function isLogoMethodMaxSizesNotActive(error: unknown): boolean {
  return error instanceof DataError && error.errorStatus?.code === 404;
}

/** The API's 409 logoMethodMaxSizeConflict: the save came from a stale page. */
export function isLogoMethodMaxSizeConflict(error: unknown): boolean {
  return (
    error instanceof DataError &&
    error.errorStatus?.code === 409 &&
    error.errorStatus.errorCode === LOGO_METHOD_MAX_SIZE_CONFLICT_ERROR_CODE
  );
}

// The 409's `state` is only trusted when it has the GET's shape.
function asMaxSizesState(value: unknown): LogoMethodMaxSizesDto | null {
  if (value === null || typeof value !== "object") return null;
  const candidate = value as Partial<LogoMethodMaxSizesDto>;
  if (!Array.isArray(candidate.methods)) return null;
  if (typeof candidate.revision !== "number") return null;
  return candidate as LogoMethodMaxSizesDto;
}

/**
 * Nothing was saved: the max sizes were changed by somebody else after the
 * page was loaded. `message` is the API's own text; `state` is the stored
 * state as the query cache holds it NOW (the very object the box's query
 * returns next), or null when it could not be loaded.
 */
export class LogoMethodMaxSizeConflictError extends Error {
  state: LogoMethodMaxSizesDto | null;
  constructor(message: string, state: LogoMethodMaxSizesDto | null) {
    super(message);
    this.name = "LogoMethodMaxSizeConflictError";
    this.state = state;
  }
}

export function useTransferMethodConfigRepository(): TransferMethodConfigRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useBlockedMethods(): Resource<string[]> {
    return useQuery(
      [BLOCKED_METHODS_KEY],
      async (): Promise<string[]> => {
        const dto = await apiClient.getBlockedTransferMethods();
        return dto.blockedMethodKeys ?? [];
      },
      {
        // Admin surface — always show the current server state on entry.
        staleTime: 0,
        // Admin perf FE-3 (flag): paint from cache + one background refetch.
        ...adminSwrOptions(),
      },
    );
  }

  async function update(blockedMethodKeys: string[]): Promise<void> {
    await apiClient.updateBlockedTransferMethods(blockedMethodKeys);
    await queryClient.invalidateQueries({ queryKey: [BLOCKED_METHODS_KEY] });
    // The customer-facing dropdowns read blockedMethodKeys from logo-options,
    // so the admin's own session must refetch it to see the change take effect.
    await queryClient.invalidateQueries({ queryKey: ["logo-options"] });
    // The method catalogs now carry a 30 min staleTime (see LogoRepository);
    // nudge transfer-options too so a method change doesn't linger in this
    // admin's session. Prefix match covers every vendorId partition.
    await queryClient.invalidateQueries({ queryKey: ["transfer-options"] });
  }

  function useMethodMaxSizes(): Resource<LogoMethodMaxSizesDto | null> {
    return useQuery(
      [LOGO_METHOD_MAX_SIZES_KEY],
      () =>
        apiClient.getLogoMethodMaxSizes().catch((error: unknown) => {
          // 404 = the API's flag is off: an honest "not active" (null) the
          // page renders as "no box", cached like an answer. Other failures
          // stay errors.
          if (isLogoMethodMaxSizesNotActive(error)) return null;
          throw error;
        }),
      {
        // Admin surface — always show the current server state on entry.
        staleTime: 0,
        // Reloading the page is the retry; the default 3 retries would turn
        // one failed visit into 4 GETs.
        retry: false,
        // Admin perf FE-3 (flag): paint from cache + one background refetch.
        ...adminSwrOptions(),
      },
    );
  }

  async function invalidateMethodCatalogs(): Promise<void> {
    // The API overrides `size` in the method lists with these values, so the
    // admin's own session must refetch the catalogs to see the change take
    // effect (30 min staleTime, see LogoRepository). Prefix match covers
    // every vendorId partition.
    await queryClient.invalidateQueries({ queryKey: ["logo-options"] });
    await queryClient.invalidateQueries({ queryKey: ["transfer-options"] });
  }

  async function updateMethodMaxSizes(
    expectedRevision: number,
    sizes: LogoMethodMaxSizeEntryDto[],
  ): Promise<LogoMethodMaxSizesDto> {
    let saved: LogoMethodMaxSizesDto;
    try {
      saved = await apiClient.updateLogoMethodMaxSizes(expectedRevision, sizes);
    } catch (error) {
      if (!isLogoMethodMaxSizeConflict(error)) throw error;
      // 409: somebody else saved after this page was loaded - nothing was
      // written. The answer carries the stored state (GET shape, with the
      // revision the next save must send): it becomes the box's state with
      // no second GET. Only an answer without a usable state costs one GET.
      const state = asMaxSizesState(
        (error as DataError).errorStatus?.logoMethodMaxSizeState,
      );
      if (state) {
        queryClient.setQueryData([LOGO_METHOD_MAX_SIZES_KEY], state);
      } else {
        await queryClient.invalidateQueries({
          queryKey: [LOGO_METHOD_MAX_SIZES_KEY],
        });
      }
      // The other save changed the limits: this session's catalogs are as
      // stale as after a save of its own. Not awaited: the admin gets the
      // message at once, the catalogs follow in the background.
      void invalidateMethodCatalogs();
      throw new LogoMethodMaxSizeConflictError(
        (error as DataError).message,
        queryClient.getQueryData<LogoMethodMaxSizesDto | null>([
          LOGO_METHOD_MAX_SIZES_KEY,
        ]) ?? null,
      );
    }
    // The PUT answers with the new server state (same shape as the GET), so
    // the box follows without a second GET.
    queryClient.setQueryData([LOGO_METHOD_MAX_SIZES_KEY], saved);
    await invalidateMethodCatalogs();
    return saved;
  }

  return {
    useBlockedMethods,
    update,
    useMethodMaxSizes,
    updateMethodMaxSizes,
  };
}
